import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { TopNavigation } from "@/components/TopNavigation";
import { Header } from "@/components/Header";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AdminRoute } from "@/components/AdminRoute";
import { AssistantBlockRoute } from "@/components/AssistantBlockRoute";
import { ViewSizeIndicator } from "@/components/ViewSizeIndicator";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { useUserRole } from "@/hooks/useUserRole";
import { NavLayoutProvider, useNavLayout } from "@/hooks/useNavLayout";
import {
  useViewSize,
  isSmallOrMedium,
  isSidebarHidden,
} from "@/hooks/useViewSize";
import { SwipeProvider } from "@/contexts/SwipeContext";
import { PageMetadataProvider } from "@/contexts/PageMetadataContext";
import { EnvironmentProvider } from "@/contexts/EnvironmentContext";
import { CoachViewProvider, useCoachView } from "@/contexts/CoachViewContext";
import { useState, useEffect, useRef, Suspense } from "react";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { LazyLoadErrorBoundary } from "@/components/LazyLoadErrorBoundary";
import { ScrollToTop } from "@/components/ScrollToTop";
import { PageLoader } from "@/components/PageLoader";
import { InstallBanner } from "@/components/InstallBanner";
import { Loader2 } from "lucide-react";
import { Navigate } from "react-router-dom";

// Eagerly load Auth page (first page users see)
import Auth from "./pages/Auth";

// Lazy load all other pages with retry logic for better performance
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"));
const Venues = lazyWithRetry(() => import("./pages/Venues"));
const VenueDetail = lazyWithRetry(() => import("./pages/VenueDetail"));
const Sessions = lazyWithRetry(() => import("./pages/Sessions"));
const SessionDetail = lazyWithRetry(() => import("./pages/SessionDetail"));
const Incidents = lazyWithRetry(() => import("./pages/Incidents"));
const IncidentDetail = lazyWithRetry(() => import("./pages/IncidentDetail"));
const Users = lazyWithRetry(() => import("./pages/Users"));
const Profile = lazyWithRetry(
  () =>
    import("./pages/Profile") as Promise<{ default: React.ComponentType<any> }>
);
const Attendance = lazyWithRetry(() => import("./pages/Attendance"));
const TakeAttendance = lazyWithRetry(() => import("./pages/TakeAttendance"));
const AttendanceApproval = lazyWithRetry(
  () => import("./pages/AttendanceApproval")
);
const CoachDocuments = lazyWithRetry(() => import("./pages/CoachDocuments"));
const DataSync = lazyWithRetry(() => import("./pages/DataSync"));
const DataSyncCallback = lazyWithRetry(
  () => import("./pages/DataSyncCallback")
);
const Settings = lazyWithRetry(() => import("./pages/Settings"));
const AuditLog = lazyWithRetry(() => import("./pages/AuditLog"));
const BulkSessionManagement = lazyWithRetry(() => import("./pages/BulkSessionManagement"));
const Mailshot = lazyWithRetry(() => import("./pages/Mailshot"));
const GDPR = lazyWithRetry(() => import("./pages/GDPR"));
const PrivacyPolicy = lazyWithRetry(() => import("./pages/PrivacyPolicy"));
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const UnpaidAttendance = lazyWithRetry(() => import("./pages/UnpaidAttendance"));
const ParticipantBook = lazyWithRetry(() => import("./pages/ParticipantBook"));
const Timesheets = lazyWithRetry(() => import("./pages/Timesheets"));
const TimesheetDetail = lazyWithRetry(() => import("./pages/TimesheetDetail"));
const TimesheetApprovals = lazyWithRetry(() => import("./pages/TimesheetApprovals"));
const Planners = lazyWithRetry(() => import("./pages/Planners"));
const PlannersAdmin = lazyWithRetry(() => import("./pages/PlannersAdmin"));
const PlannerDetail = lazyWithRetry(() => import("./pages/PlannerDetail"));
const Tournaments = lazyWithRetry(() => import("./pages/Tournaments"));
const TournamentNew = lazyWithRetry(() => import("./pages/TournamentNew"));
const TournamentDetail = lazyWithRetry(() => import("./pages/TournamentDetail"));
const TournamentApprovals = lazyWithRetry(() => import("./pages/TournamentApprovals"));
const Holidays = lazyWithRetry(() => import("./pages/Holidays"));

const queryClient = new QueryClient();

function ResponsiveSidebarLayout({
  children,
  isAdmin,
  isCoach,
  isAssociate,
  isAssistant,
  profile,
}: {
  children: React.ReactNode;
  isAdmin: boolean;
  isCoach: boolean;
  isAssociate: boolean;
  isAssistant: boolean;
  profile: any;
}) {
  const viewSize = useViewSize();
  const isSmall = isSmallOrMedium(viewSize);
  const sidebarHidden = isSidebarHidden(viewSize);

  // Read cookie to get persisted sidebar state
  const getSidebarCookie = () => {
    const name = "sidebar:state=";
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(";");
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === " ") {
        c = c.substring(1);
      }
      if (c.indexOf(name) === 0) {
        return c.substring(name.length, c.length) === "true";
      }
    }
    return true; // default to open
  };

  const [sidebarOpen, setSidebarOpen] = useState(() => {
    // On small screens, always collapse
    if (isSmall) return false;
    // On large screens, use cookie state
    return getSidebarCookie();
  });

  const prevIsSmall = useRef(isSmall);

  useEffect(() => {
    // Only collapse when transitioning to small screen
    if (!prevIsSmall.current && isSmall) {
      setSidebarOpen(false);
    }
    // When transitioning from small to large, read cookie state
    else if (prevIsSmall.current && !isSmall) {
      setSidebarOpen(getSidebarCookie());
    }
    prevIsSmall.current = isSmall;
  }, [isSmall]);

  const handleOpenChange = (open: boolean) => {
    setSidebarOpen(open);
  };

  return (
    <SidebarProvider open={sidebarOpen} onOpenChange={handleOpenChange}>
      <div className="h-[100svh] max-h-[100svh] overflow-hidden flex w-full">
        <AppSidebar isAdmin={isAdmin} isCoach={isCoach} isAssociate={isAssociate} isAssistant={isAssistant} profile={profile} />
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <Header
            show={sidebarHidden}
            isAdmin={isAdmin}
            isCoach={isCoach}
            isAssistant={isAssistant}
          />
          <main className="flex-1 min-h-0 flex flex-col overflow-hidden">
            {children}
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}

function AppLayout({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { profile } = useProfile(user?.id);
  const { isAdmin, isCoach, isAssociate, isAssistant } = useUserRole(user?.id);
  const { layout } = useNavLayout();

  if (!user || !profile) return null;

  return (
    <CoachViewProvider isAdmin={isAdmin}>
      <EffectiveLayout
        isAdmin={isAdmin}
        isCoach={isCoach}
        isAssociate={isAssociate}
        isAssistant={isAssistant}
        profile={profile}
        layout={layout}
      >
        {children}
      </EffectiveLayout>
    </CoachViewProvider>
  );
}

function EffectiveLayout({
  children,
  isAdmin,
  isCoach,
  isAssociate,
  isAssistant,
  profile,
  layout,
}: {
  children: React.ReactNode;
  isAdmin: boolean;
  isCoach: boolean;
  isAssociate: boolean;
  isAssistant: boolean;
  profile: any;
  layout: ReturnType<typeof useNavLayout>["layout"];
}) {
  const { viewMode, viewAsCoach } = useCoachView();
  const effectiveIsAdmin = isAdmin && viewMode === "admin";
  // Assistants are NOT coaches (no attendance access). Keep them separate.
  const effectiveIsCoach = isCoach || isAssociate || viewAsCoach;

  if (layout === "top") {
    return (
      <div className="h-[100svh] max-h-[100svh] overflow-hidden flex flex-col w-full">
        <TopNavigation
          isAdmin={effectiveIsAdmin}
          isCoach={effectiveIsCoach}
          isAssistant={isAssistant}
          profile={profile}
        />
        <main className="flex-1 min-h-0 overflow-hidden flex flex-col">{children}</main>
      </div>
    );
  }

  return (
    <ResponsiveSidebarLayout
      isAdmin={effectiveIsAdmin}
      isCoach={effectiveIsCoach}
      isAssociate={isAssociate}
      isAssistant={isAssistant}
      profile={profile}
    >
      {children}
    </ResponsiveSidebarLayout>
  );
}

function LandingRoute() {
  const { user, loading: authLoading } = useAuth();
  const { profile, loading: profileLoading } = useProfile(user?.id);
  const { isAdmin, isCoach, isAssociate, isAssistant, loading: roleLoading, loadedForUserId } =
    useUserRole(user?.id);
  const { viewAsCoach } = useCoachView();

  const stillLoading =
    authLoading ||
    profileLoading ||
    roleLoading ||
    (user?.id && loadedForUserId !== user.id);

  if (stillLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Priority:
  // 1) Assistant (non-admin) -> Planners (no attendance access)
  // 2) Admin viewing as coach, or coach/associate non-admin -> Attendance
  // 3) Otherwise admin -> Dashboard
  if (isAssistant && !isAdmin && !isCoach && !isAssociate) {
    return <Navigate to="/planners" replace />;
  }
  if (viewAsCoach || (isCoach && !isAdmin) || isAssociate) {
    return <Navigate to="/attendance" replace />;
  }

  return <Dashboard />;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
      <NavLayoutProvider>
        <PageMetadataProvider>
          <EnvironmentProvider>
            <SwipeProvider>
              <TooltipProvider>
                <Toaster />
                <Sonner />
                <ViewSizeIndicator />
                <InstallBanner />
                <BrowserRouter>
                  <ScrollToTop />
                  <LazyLoadErrorBoundary>
                    <Suspense fallback={<PageLoader />}>
                      <Routes>
                        <Route path="/auth" element={<Auth />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="/gdpr" element={<GDPR />} />
                        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                        <Route path="/admin" element={<Navigate to="/" replace />} />
                        <Route
                          path="/dashboard"
                          element={
                            <ProtectedRoute>
                              <AppLayout>
                                <Dashboard />
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/"
                          element={
                            <ProtectedRoute>
                              <AppLayout>
                                <LandingRoute />
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/venues"
                          element={
                            <ProtectedRoute>
                              <AppLayout>
                                <Venues />
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/venues/:id"
                          element={
                            <ProtectedRoute>
                              <AppLayout>
                                <VenueDetail />
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/sessions"
                          element={
                            <ProtectedRoute>
                              <AppLayout>
                                <Sessions />
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/sessions/:id"
                          element={
                            <ProtectedRoute>
                              <AppLayout>
                                <SessionDetail />
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/incidents"
                          element={
                            <ProtectedRoute>
                              <AppLayout>
                                <Incidents />
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/incidents/:id"
                          element={
                            <ProtectedRoute>
                              <AppLayout>
                                <IncidentDetail />
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/users"
                          element={
                            <ProtectedRoute>
                              <AdminRoute>
                                <AppLayout>
                                  <Users />
                                </AppLayout>
                              </AdminRoute>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/profile"
                          element={
                            <ProtectedRoute>
                              <AppLayout>
                                <Profile />
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/profile/:userId"
                          element={
                            <ProtectedRoute>
                              <AppLayout>
                                <Profile />
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/attendance"
                          element={
                            <ProtectedRoute>
                              <AssistantBlockRoute>
                                <AppLayout>
                                  <Attendance />
                                </AppLayout>
                              </AssistantBlockRoute>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/attendance/:id"
                          element={
                            <ProtectedRoute>
                              <AssistantBlockRoute>
                                <AppLayout>
                                  <TakeAttendance />
                                </AppLayout>
                              </AssistantBlockRoute>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/attendance-approval/:id?"
                          element={
                            <ProtectedRoute>
                              <AdminRoute>
                                <AppLayout>
                                  <AttendanceApproval />
                                </AppLayout>
                              </AdminRoute>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/coach-documents"
                          element={
                            <ProtectedRoute>
                              <AppLayout>
                                <CoachDocuments />
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/data-sync"
                          element={
                            <ProtectedRoute>
                              <AdminRoute>
                                <AppLayout>
                                  <DataSync />
                                </AppLayout>
                              </AdminRoute>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/data-sync/callback"
                          element={
                            <ProtectedRoute>
                              <DataSyncCallback />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/settings"
                          element={
                            <ProtectedRoute>
                              <AdminRoute>
                                <AppLayout>
                                  <Settings />
                                </AppLayout>
                              </AdminRoute>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/audit-log"
                          element={
                            <ProtectedRoute>
                              <AdminRoute>
                                <AppLayout>
                                  <AuditLog />
                                </AppLayout>
                              </AdminRoute>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/bulk-sessions"
                          element={
                            <ProtectedRoute>
                              <AdminRoute>
                                <AppLayout>
                                  <BulkSessionManagement />
                                </AppLayout>
                              </AdminRoute>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/unpaid-attendance"
                          element={
                            <ProtectedRoute>
                              <AdminRoute>
                                <AppLayout>
                                  <UnpaidAttendance />
                                </AppLayout>
                              </AdminRoute>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/mailshot"
                          element={
                            <ProtectedRoute>
                              <AdminRoute>
                                <AppLayout>
                                  <Mailshot />
                                </AppLayout>
                              </AdminRoute>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/participant-book"
                          element={
                            <ProtectedRoute>
                              <AdminRoute>
                                <AppLayout>
                                  <ParticipantBook />
                                </AppLayout>
                              </AdminRoute>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/timesheets"
                          element={
                            <ProtectedRoute>
                              <AppLayout>
                                <Timesheets />
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/timesheets/approvals"
                          element={
                            <ProtectedRoute>
                              <AdminRoute>
                                <AppLayout>
                                  <TimesheetApprovals />
                                </AppLayout>
                              </AdminRoute>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/timesheets/:id"
                          element={
                            <ProtectedRoute>
                              <AppLayout>
                                <TimesheetDetail />
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/planners"
                          element={
                            <ProtectedRoute>
                              <AppLayout>
                                <Planners />
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/planners/admin"
                          element={
                            <ProtectedRoute>
                              <AdminRoute>
                                <AppLayout>
                                  <PlannersAdmin />
                                </AppLayout>
                              </AdminRoute>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/planners/:id"
                          element={
                            <ProtectedRoute>
                              <AppLayout>
                                <PlannerDetail />
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/tournaments"
                          element={
                            <ProtectedRoute>
                              <AppLayout>
                                <Tournaments />
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/tournaments/new"
                          element={
                            <ProtectedRoute>
                              <AppLayout>
                                <TournamentNew />
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/tournaments/approvals"
                          element={
                            <ProtectedRoute>
                              <AdminRoute>
                                <AppLayout>
                                  <TournamentApprovals />
                                </AppLayout>
                              </AdminRoute>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/tournaments/:id"
                          element={
                            <ProtectedRoute>
                              <AppLayout>
                                <TournamentDetail />
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/holidays"
                          element={
                            <ProtectedRoute>
                              <AppLayout>
                                <Holidays />
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </Suspense>
                  </LazyLoadErrorBoundary>
                </BrowserRouter>
              </TooltipProvider>
            </SwipeProvider>
          </EnvironmentProvider>
        </PageMetadataProvider>
      </NavLayoutProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
